import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { MessageCircle, ArrowRight, MapPin, Wrench, ShieldCheck, Zap } from "lucide-react";
import { motion } from "framer-motion";
import LiquidGoldCanvas from "@/components/LiquidGoldCanvas";
import { useLanguage } from "@/hooks/useLanguage";
import { COMPANY } from "@/data/site";

export default function HeroSection() {
  const { t, lang } = useLanguage();
  const headingRef = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    const text = headingRef.current;
    if (!text) return;

    const originalText = text.textContent || "";
    const lines = originalText.split("\n").filter((l) => l.trim());

    text.innerHTML = lines
      .map(
        (line) =>
          `<span style="display:inline-block;overflow:hidden;vertical-align:top;line-height:1.2em;margin-bottom:-0.2em;"><span style="display:inline-block;will-change:transform;">${line.trim()}</span></span>`
      )
      .join(" ");

    const innerSpans = text.querySelectorAll("span span");
    innerSpans.forEach((span, i) => {
      const el = span as HTMLElement;
      el.style.transform = "translateY(125%)";
      el.style.transition = `transform 1.2s cubic-bezier(0.25, 0.46, 0.45, 0.94) ${0.3 + i * 0.15}s`;
      void el.offsetHeight;
      el.style.transform = "translateY(0)";
    });

    return () => {
      text.innerHTML = originalText;
    };
  }, [lang]);

  return (
    <section className="relative min-h-[100dvh] flex items-center overflow-hidden">
      <LiquidGoldCanvas />
      <div
        className="absolute inset-0 z-[1]"
        style={{ background: "linear-gradient(135deg, rgba(10,46,76,0.85) 0%, rgba(10,46,76,0.70) 50%, rgba(17,75,138,0.50) 100%)" }}
      />

      <div className="relative z-[2] content-container px-4 sm:px-6 lg:px-8 py-24 pt-32 w-full">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-7">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.4 }}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 mb-6"
            >
              <MapPin className="w-4 h-4 text-gold" />
              <span className="text-sm font-medium text-white/90 tracking-wide">{t("heroLocation")}</span>
            </motion.div>

            <h1
              ref={headingRef}
              className="font-display font-extrabold text-white text-4xl sm:text-5xl md:text-6xl lg:text-7xl leading-[1.05] mb-6"
            >
              {t("heroTitleLine1")}
              {t("heroTitleLine2")}
              {t("heroTitleLine3")}
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.6 }}
              className="text-lg md:text-xl text-white/80 leading-relaxed max-w-xl mb-8"
            >
              {t("heroSubtitle")}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 1.0 }}
              className="flex flex-wrap gap-3"
            >
              <Link to="/capabilities" className="inline-flex items-center gap-2 px-7 py-3.5 rounded-lg bg-white text-primary-deep font-body font-bold text-base transition-all duration-300 hover:bg-gold-base hover:text-white hover:shadow-xl">
                {t("exploreCapabilities")} <ArrowRight className="w-4 h-4" />
              </Link>
              <a href={COMPANY.whatsapp} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-7 py-3.5 rounded-lg bg-success/90 backdrop-blur-sm text-white font-body font-bold text-base transition-all duration-300 hover:bg-success hover:shadow-lg">
                <MessageCircle className="w-5 h-5" />
                {t("whatsappInquiry")}
              </a>
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.8 }}
            className="lg:col-span-5 hidden lg:block"
          >
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-xl p-6">
                <Wrench className="w-6 h-6 text-gold mb-3" />
                <div className="font-display font-extrabold text-3xl text-white">10+</div>
                <div className="text-sm text-white/60 mt-1">{t("serviceAreas")}</div>
              </div>
              <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-xl p-6">
                <ShieldCheck className="w-6 h-6 text-gold mb-3" />
                <div className="font-display font-extrabold text-3xl text-white">100%</div>
                <div className="text-sm text-white/60 mt-1">{t("projectFocused")}</div>
              </div>
              <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-xl p-6">
                <Zap className="w-6 h-6 text-gold mb-3" />
                <div className="font-display font-extrabold text-3xl text-white">CNC</div>
                <div className="text-sm text-white/60 mt-1">Laser + Bending</div>
              </div>
              <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-xl p-6">
                <MapPin className="w-6 h-6 text-gold mb-3" />
                <div className="font-display font-extrabold text-3xl text-white">SA</div>
                <div className="text-sm text-white/60 mt-1">Nationwide Supply</div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 2, duration: 0.5 }} className="absolute bottom-8 left-1/2 -translate-x-1/2 z-[2]">
        <div className="w-px h-12 bg-white/30 relative overflow-hidden">
          <div className="absolute top-0 left-0 w-full h-4 bg-white/60 animate-bounce" />
        </div>
      </motion.div>
    </section>
  );
}
